import {
  Box,
  Link,
  Button,
  Container,
  Header,
  Toggle,
  TextContent,
} from '@amzn/awsui-components-core';
import { ReviewRequestStatus, WorkflowState } from '@amzn/awscat-pattern-library-service-model';
import React, { useContext, useState } from 'react';
import { AttachmentList } from '../../common/AttachmentList';
import ReviewFeedback from '../review-feedback/ReviewFeedback';
import CodeRepoList from './CodeRepoList';
import { ContentDataProps } from './ContentData';
import {
  isAuthorOrCoAuthor,
  isInPeerReview,
  isAllowedToSeeAppSecLink,
} from '../../utils/ContentDataUtils';
import DraftViewerRole from '../../../components/utils/DraftViewerRole';
import ReviewerChecklist from './ReviewerChecklist';
import constants from '../../../common/constants';
import ContentAudienceBadge from '../ContentAudienceBadge';
import ContentDataControls from './ContentDataControls';
import { PublicationStateBadge } from '../../publication/PublicationStateBadge';
import TemplateMetadataForm from '../../content/TemplateMetadataForm';
import { ContentSchemaSection } from '../../../model/ContentSchemaSection';
import * as UtilApi from '../../../api/UtilsApi';
import { ReadViewTab } from './ReadonlyContentData';
import { ContentViewerContext } from '../ContentViewerContext';
import ContentQualityEvalucationCard from '../../content-quality/ContentQualityEvaluationCard';
import { InlineCommentList, ShowAllCommentsButton } from '../../inline-comment';

const ReadonlyContentRightColumn: React.FunctionComponent<ContentDataProps> = (
  props: ContentDataProps,
) => {
  const {
    actionForm,
    appContext,
    coAuthors,
    contentOwner,
    content,
    contentAudience,
    contentId,
    draftViewerRole,
    onEnableEditMode,
    onReviewComplete,
    onTabViewChange,
    peerReviewers,
    publicationState,
    showContentControls,
    showReviewFeedback,
    submittingForReview,
    workflow,
    workflowEvents,
    workflowHistory,
    approvalDisabled,
    disableApproveMessage,
    showLastBarRaiserEvaluation,
    isCommentingModeEnabled,
    enableCommentMode,
  } = props;
  const [showAllComents, setShowAllComments] = useState<boolean>(false);
  const { contentMetadata, contentTypeMetadata, templateMetadata } = content;
  const { contentType } = contentMetadata;
  // @ts-expect-error TS2339 - strict migration
  const { userProfile } = appContext.authnInfo;
  const { reviewRequestStatus } = useContext(ContentViewerContext);

  const isEditingEnabled: boolean = false;
  const reviewerRoles = [
    DraftViewerRole.Admin,
    DraftViewerRole.TechWriter,
    DraftViewerRole.Reviewer,
  ];
  const allowedToSeeAppSecLink = isAllowedToSeeAppSecLink(
    userProfile,
    contentOwner,
    workflow,
    coAuthors,
    // @ts-expect-error TS2345 - strict migration
    peerReviewers,
  );

  const getContentDataControlProps = () => {
    return {
      contentType: contentMetadata.contentType,
      disableButtons: submittingForReview,
      isEditingEnabled,
      isSaveDisabled: true,
      onEnableEditing: onEnableEditMode,
      workingCopyStatus: null,
      isInPeerReview: isInPeerReview(workflow),
    };
  };

  const handleShowAllComments = () => {
    setShowAllComments(true);
  };

  // @ts-expect-error TS7031 - strict migration
  const handleEnableCommentingMode = ({ detail }) => {
    // @ts-expect-error TS2722 - strict migration
    enableCommentMode(detail.checked);
  };

  const renderControlButtons = () => {
    const showControls =
      !workflow || !workflow.lock || workflow.lock.acquiredBy === userProfile.userId;
    const showEditButton =
      showContentControls ||
      (isAuthorOrCoAuthor({ userProfile, coAuthors, contentOwner, workflow }) &&
        draftViewerRole !== DraftViewerRole.Reviewer &&
        isInPeerReview(workflow));
    const inlineCommentsControl = (
      <Box padding={{ top: 'xxs' }}>
        <ShowAllCommentsButton handleShowAllComments={handleShowAllComments} />
      </Box>
    );
    return showControls && showEditButton ? (
      <div className="readonly-action-container">
        <Container header={<Header variant="h3">Actions</Header>}>
          <div className="control-button">
            {/* @ts-expect-error TS2769 - strict migration */}
            <ContentDataControls {...getContentDataControlProps()} onlyActionButtons />
            {inlineCommentsControl}
          </div>
        </Container>
      </div>
    ) : null;
  };

  const handlePreviousVersionLink = () => {
    const tabId = ReadViewTab.History;
    if (onTabViewChange) {
      onTabViewChange(tabId);
    }
  };

  const renderReviewerChecklist = () => {
    const workflowInReview =
      !!workflow &&
      workflow.draftState === WorkflowState.IN_REVIEW &&
      reviewRequestStatus === ReviewRequestStatus.ACCEPTED;
    const reviewerChecklist =
      workflowInReview && reviewerRoles.includes(draftViewerRole) ? (
        <ReviewerChecklist
          // @ts-expect-error TS2322 - strict migration
          onReviewComplete={onReviewComplete}
          workflow={workflow}
          approvalDisabled={approvalDisabled}
          disableApproveMessage={disableApproveMessage}
        />
      ) : null;
    return reviewerChecklist;
  };

  const readonlyTemplateMetadataForm = (
    <TemplateMetadataForm
      templateSchema={UtilApi.getContentTemplateSchema(
        contentType,
        contentTypeMetadata,
        ContentSchemaSection.TemplateMetadata,
      )}
      visible
      templateData={templateMetadata}
      readonly={!isEditingEnabled}
    />
  );

  const visibilityMetadata =
    publicationState && workflowHistory?.length ? (
      <details id="visibility" open>
        <summary>{constants.CONTENT_VISIBILITY_LABEL}</summary>
        <TextContent>
          <ul>
            <li>
              {' '}
              {constants.CONTENT_VISIBILITY_CURRENT_VERSION_LABEL}{' '}
              <PublicationStateBadge state={publicationState} />{' '}
            </li>
            <li>
              {' '}
              <Link onFollow={handlePreviousVersionLink}>
                {constants.CONTENT_VISIBILITY_PREVIOUS_VERSION_LABEL}
              </Link>{' '}
            </li>
          </ul>
        </TextContent>
      </details>
    ) : null;

  const contentReadonlyMetadata = (
    <article id="content-details">
      <header>
        <Box variant="h5">{contentMetadata.contentType} details</Box>
      </header>
      {readonlyTemplateMetadataForm}
      <details id="intended-audience" open>
        <summary>{constants.CONTENT_AUDIENCE_LABEL}</summary>
        <ContentAudienceBadge contentAudience={contentAudience} />
      </details>
      {visibilityMetadata}
    </article>
  );

  const readonlyCodeLinks = !content.contentTypeMetadata.codeLinks?.length ? null : (
    <CodeRepoList
      userType={userProfile?.type}
      isAllowedToSeeAppSecLink={allowedToSeeAppSecLink}
      codeLinks={content.contentTypeMetadata.codeLinks}
      // @ts-expect-error TS2322 - strict migration
      handleAddLink={null}
      readOnly
    />
  );

  // @ts-expect-error TS18048 - strict migration
  const readonlyAttachmentList = !content.attachments.length ? null : (
    <AttachmentList
      // @ts-expect-error TS2783 - strict migration
      contentId={contentId}
      // @ts-expect-error TS2322 - strict migration
      attachmentIds={content.attachments}
      readOnly
      // @ts-expect-error TS2322 - strict migration
      onUpdate={null}
      // @ts-expect-error TS2322 - strict migration
      onDelete={null}
      {...props}
    />
  );

  const reviewFeedback = (
    <Box margin={{ vertical: 'm' }}>
      <ReviewFeedback
        // @ts-expect-error TS2322 - strict migration
        workflowEvents={workflowEvents}
        reviewerUserIds={[]}
        visible={showReviewFeedback}
      />
    </Box>
  );

  const barRaiserScorecard = !!showLastBarRaiserEvaluation ? (
    <Box margin={{ vertical: 'm' }}>
      <ContentQualityEvalucationCard contentId={contentId} readonly />
    </Box>
  ) : null;

  if (!showAllComents) {
    return (
      <>
        {renderControlButtons()}
        <div id="component-column">
          {contentReadonlyMetadata}
          {readonlyCodeLinks}
          {readonlyAttachmentList}
          {reviewFeedback}
          {barRaiserScorecard}
          {actionForm}
          {renderReviewerChecklist()}
        </div>
      </>
    );
  } else {
    return (
      <InlineCommentList
        setShowAllComment={setShowAllComments}
        contentId={contentId}
        workflowId={workflow?.draftWorkflowId}
      />
    );
  }
};

export default ReadonlyContentRightColumn;
